// Voice→SOAP shared contract — source of truth for FE (Freya) + BE (Hades).
// See /Users/tanawat/nova-oracle/ψ/outbox/contract_yui_voicesoap.md
// Field naming: camelCase everywhere. Dates: ISO 8601 UTC. IDs: integers.

import { z } from 'zod';

// ============================================================================
// Primitives
// ============================================================================

export const Confidence = z.enum(['high', 'medium', 'low']);
export type Confidence = z.infer<typeof Confidence>;

export const MissingReason = z.enum([
  'not_mentioned',
  'unclear',
  'not_applicable',
  '',
]);
export type MissingReason = z.infer<typeof MissingReason>;

export const Side = z.enum(['left', 'right', 'bilateral', 'n/a']);
export type Side = z.infer<typeof Side>;

export const VisitType = z.enum(['initial', 'followup']);
export type VisitType = z.infer<typeof VisitType>;

// ============================================================================
// SoapField — every extracted value carries provenance
// ============================================================================

export const SoapStringField = z.object({
  value: z.string().nullable(),
  sourcePhrase: z.string(),
  confidence: Confidence,
  missingReason: MissingReason.optional(),
  transcriptStart: z.number().nullable().optional(),
});
export type SoapStringField = z.infer<typeof SoapStringField>;

export const SoapNumberField = z.object({
  value: z.number().nullable(),
  sourcePhrase: z.string(),
  confidence: Confidence,
  missingReason: MissingReason.optional(),
  transcriptStart: z.number().nullable().optional(),
});
export type SoapNumberField = z.infer<typeof SoapNumberField>;

// ============================================================================
// Subjective
// ============================================================================

export const SubjectiveSchema = z.object({
  chiefComplaint: SoapStringField,
  painVas: z.object({
    value: z.number().min(0).max(10).nullable(),
    sourcePhrase: z.string(),
    confidence: Confidence,
    missingReason: MissingReason.optional(),
    transcriptStart: z.number().nullable().optional(),
  }),
  painLocation: SoapStringField,
  painQuality: SoapStringField,
  onset: SoapStringField,
  aggravatingFactors: z.array(z.string()),
  relievingFactors: z.array(z.string()),
  priorLevelOfFunction: SoapStringField,
  patientGoals: z.array(z.string()),
  pmh: SoapStringField,
  medications: z.array(z.string()),
  allergies: z.array(z.string()),
});
export type SubjectiveSchema = z.infer<typeof SubjectiveSchema>;

// ============================================================================
// Objective
// ============================================================================

export const VitalsSchema = z.object({
  bpSystolic: SoapNumberField,
  bpDiastolic: SoapNumberField,
  pulse: SoapNumberField,
  rr: SoapNumberField,
  tempC: SoapNumberField,
  spo2: SoapNumberField,
});
export type VitalsSchema = z.infer<typeof VitalsSchema>;

export const RomEntry = z.object({
  joint: z.string(),
  movement: z.string(),
  side: Side,
  degrees: z.number().nullable(),
  sourcePhrase: z.string(),
  transcriptStart: z.number().nullable().optional(),
});
export type RomEntry = z.infer<typeof RomEntry>;

export const MmtEntry = z.object({
  muscle: z.string(),
  side: Side,
  grade: z.number().min(0).max(5).nullable(),
  sourcePhrase: z.string(),
  transcriptStart: z.number().nullable().optional(),
});
export type MmtEntry = z.infer<typeof MmtEntry>;

export const SpecialTestResult = z.enum(['positive', 'negative', 'inconclusive']);
export type SpecialTestResult = z.infer<typeof SpecialTestResult>;

export const SpecialTestEntry = z.object({
  testName: z.string(),
  result: SpecialTestResult,
  note: z.string().nullable(),
  sourcePhrase: z.string(),
  transcriptStart: z.number().nullable().optional(),
});
export type SpecialTestEntry = z.infer<typeof SpecialTestEntry>;

export const InterventionType = z.enum(['modality', 'exercise', 'manual', 'education']);
export type InterventionType = z.infer<typeof InterventionType>;

export const InterventionEntry = z.object({
  type: InterventionType,
  name: z.string(),
  parameters: z.string(),
  region: z.string(),
  sourcePhrase: z.string(),
  transcriptStart: z.number().nullable().optional(),
});
export type InterventionEntry = z.infer<typeof InterventionEntry>;

export const OutcomeMetric = z.enum([
  'tug_sec',
  'six_mwt_m',
  'bbs',
  'odi_percent',
  'ndi_percent',
  'grip_kg',
  'ten_mwt_ms',
  'vas_pain',
]);
export type OutcomeMetric = z.infer<typeof OutcomeMetric>;

export const OutcomeMeasureEntry = z.object({
  metric: OutcomeMetric,
  value: z.number().nullable(),
  unit: z.string(),
  sourcePhrase: z.string(),
});
export type OutcomeMeasureEntry = z.infer<typeof OutcomeMeasureEntry>;

export const ObjectiveSchema = z.object({
  vitals: VitalsSchema,
  observation: SoapStringField,
  rom: z.array(RomEntry),
  mmt: z.array(MmtEntry),
  specialTests: z.array(SpecialTestEntry),
  interventionsToday: z.array(InterventionEntry),
  outcomeMeasures: z.array(OutcomeMeasureEntry),
});
export type ObjectiveSchema = z.infer<typeof ObjectiveSchema>;

// ============================================================================
// Assessment
// ============================================================================

export const AssessmentSchema = z.object({
  ptDiagnosis: SoapStringField,
  clinicalReasoning: SoapStringField,
  progressNote: SoapStringField, // followup — interval change
  skilledRationale: SoapStringField,
});
export type AssessmentSchema = z.infer<typeof AssessmentSchema>;

// ============================================================================
// Plan
// ============================================================================

export const GoalType = z.enum(['short_term', 'long_term']);
export type GoalType = z.infer<typeof GoalType>;

export const GoalEntry = z.object({
  type: GoalType,
  targetActivity: z.string(),
  metric: z.string().nullable(),
  from: z.number().nullable(),
  to: z.number().nullable(),
  unit: z.string().nullable(),
  timeframeWeeks: z.number().nullable(),
  sourcePhrase: z.string(),
});
export type GoalEntry = z.infer<typeof GoalEntry>;

export const PlanSchema = z.object({
  goals: z.array(GoalEntry),
  interventionsPlanned: z.array(z.string()),
  frequency: SoapStringField,
  durationWeeks: SoapNumberField,
  hep: z.array(z.string()),
  patientEducation: z.array(z.string()),
  nextVisit: SoapStringField,
});
export type PlanSchema = z.infer<typeof PlanSchema>;

// ============================================================================
// Review flags + full payload
// ============================================================================

export const ReviewFlag = z.enum([
  'low_confidence_vitals',
  'unclear_rom',
  'missing_vas',
  'english_heavy',
  'short_audio',
  'number_not_found_in_transcript',
  'safety_block_partial',
]);
export type ReviewFlag = z.infer<typeof ReviewFlag>;

export const SoapPayloadSchema = z.object({
  visitType: VisitType,
  transcriptSummary: z.string(),
  subjective: SubjectiveSchema,
  objective: ObjectiveSchema,
  assessment: AssessmentSchema,
  plan: PlanSchema,
  reviewFlags: z.array(ReviewFlag),
});
export type SoapPayload = z.infer<typeof SoapPayloadSchema>;

// ============================================================================
// Patient + Visit
// ============================================================================

export interface Patient {
  id: number;
  customerId: number | null;
  nationalId: string | null;
  hn: string;
  nameTh: string;
  nameEn: string | null;
  dob: string | null;
  sex: 'M' | 'F' | null;
  phone: string | null;
  email: string | null;
  emergencyContact: string | null;
  heightCm: number | null;
  weightKg: number | null;
  pmh: string | null;
  allergies: string | null;
  occupation: string | null;
  address: string | null;
  consentAudio: boolean;
  consentAiScribe: boolean;
  createdAt: string;
}

export interface CreatePatientRequest {
  customerId?: number | null;
  nationalId?: string | null;
  hn: string;
  nameTh: string;
  nameEn?: string | null;
  dob?: string | null;
  sex?: 'M' | 'F' | null;
  phone?: string | null;
  email?: string | null;
  emergencyContact?: string | null;
  heightCm?: number | null;
  weightKg?: number | null;
  pmh?: string | null;
  allergies?: string | null;
  occupation?: string | null;
  address?: string | null;
  consentAudio?: boolean;
  consentAiScribe?: boolean;
}

export interface Visit {
  id: number;
  patientId: number;
  patientNameSnapshot: string;
  patientHnSnapshot: string;
  visitType: VisitType;
  visitDate: string;
  timeIn: string | null;
  timeOut: string | null;
  therapistId: number;
  createdAt: string;
}

// therapistId is derived server-side from the authenticated JWT; clients
// must NOT send it. A request body carrying a therapistId is rejected by
// the API to prevent user A from creating a visit attributed to user B.
export interface CreateVisitRequest {
  patientId: number;
  visitType: VisitType;
}

// ============================================================================
// Audio + Transcript + Draft + Final
// ============================================================================

export interface AudioRecording {
  id: number;
  visitId: number;
  mimeType: string;
  durationSec: number | null;
  sizeBytes: number | null;
  sha256: string;
  createdAt: string;
}

export interface TranscriptSegment {
  text: string;
  startSec: number;
  endSec: number;
}

export interface Transcript {
  id: number;
  text: string;
  segmentsJson?: TranscriptSegment[];
  wordsJson?: unknown[];
  model: string;
  language?: string;
  promptUsed?: string;
  createdAt: string;
}

export interface SoapDraft {
  id: number;
  json: SoapPayload;
  reviewFlags: ReviewFlag[];
  model?: string;
  escalatedModel?: string | null;
  latencyMs?: { total: number } | Record<string, number>;
  createdAt: string;
}

export interface SoapFinal {
  id: number;
  visitId: number;
  draftId: number;
  therapistId: number;
  therapistLicense: string;
  attestedAt: string;
  deviceFingerprint?: string | null;
  noteHash: string;
  editDurationSec?: number | null;
  voidedAt?: string | null;
  voidReason?: string | null;
  voidedBy?: number | null;
  attestationVersion: number;
  json?: SoapPayload;
  diffJson?: Record<string, { before: unknown; after: unknown }>;
}

// ============================================================================
// Endpoint request/response envelopes
// ============================================================================

export interface PainLocation {
  x: number;
  y: number;
  side: 'front' | 'back';
}

export interface ProgressionNote {
  id: number;
  visitId: number;
  patientId: number;
  therapistId: number;
  bpSystolic: number | null;
  bpDiastolic: number | null;
  hr: number | null;
  spo2: number | null;
  tempC: number | null;
  vasScore: number | null;
  noteText: string | null;
  diagnosis: string | null;
  chiefComplaint: string | null;
  subjectiveExam: string | null;
  objectiveExam: string | null;
  assessment: string | null;
  planGoal: string | null;
  treatment: string | null;
  travelHistory: string | null;
  tempSensation: string | null;
  painLocations: PainLocation[];
  therapistSignature: string | null;
  createdAt: string;
}

export interface CreateProgressionNoteRequest {
  bpSystolic?: number | null;
  bpDiastolic?: number | null;
  hr?: number | null;
  spo2?: number | null;
  tempC?: number | null;
  vasScore?: number | null;
  noteText?: string | null;
  diagnosis?: string | null;
  chiefComplaint?: string | null;
  subjectiveExam?: string | null;
  objectiveExam?: string | null;
  assessment?: string | null;
  planGoal?: string | null;
  treatment?: string | null;
  travelHistory?: string | null;
  tempSensation?: string | null;
  painLocations?: PainLocation[];
  therapistSignature?: string | null;
}

export interface VisitFullResponse {
  visit: Visit;
  patient: Patient | null;
  audio: AudioRecording | null;
  transcript: Transcript | null;
  draft: SoapDraft | null;
  final: SoapFinal | null;
  progressionNote: ProgressionNote | null;
}

// List endpoint: query params `from`, `to` (YYYY-MM-DD), `q` (partial
// match on patient name snapshot OR HN snapshot), `patientId` (exact).
// `total` is optional — included only when the caller asks for paging
// metadata (cheap COUNT on the same filters).
export interface ListVisitsResponse {
  visits: Visit[];
  total?: number;
}

// Patient detail embeds the latest N visits (default 20, newest-first) so
// the FE patient card can render without a second round-trip.
export interface PatientDetailResponse {
  patient: Patient;
  visits: Visit[];
}

export interface UploadAudioResponse {
  audio: AudioRecording;
}

export interface TranscribeAndExtractRequest {
  audioId: number;
}

export interface TranscribeAndExtractResponse {
  transcript: Transcript;
  draft: SoapDraft;
}

export interface DraftResponse {
  draftId: number;
  json: SoapPayload;
  transcript: {
    text: string;
    segmentsJson: TranscriptSegment[];
    wordsJson: unknown[];
  };
  audio: { url: string; durationSec: number | null };
  reviewFlags: ReviewFlag[];
  needsEscalation: boolean;
}

export interface EscalateRequest {
  draftId: number;
}

export interface EscalateResponse {
  draft: SoapDraft;
}

export interface NoteEditRequest {
  fieldPath: string;
  before: string | null;
  after: string | null;
}

export interface NoteEdit {
  id: number;
  visitId: number;
  fieldPath: string;
  before: string | null;
  after: string | null;
  editedAt: string;
  editedBy: number;
}

export interface NoteEditResponse {
  edit: NoteEdit;
}

export interface FinalizeRequest {
  draftId: number;
  json: SoapPayload;
  deviceFingerprint: string;
  editDurationSec: number;
}

export interface FinalizeResponse {
  final: SoapFinal;
}

export interface VoidRequest {
  reason: string;
}

export interface VoidResponse {
  final: Pick<
    SoapFinal,
    'id' | 'voidedAt' | 'voidReason' | 'voidedBy'
  >;
}

export interface ProgressPoint {
  visitId: number;
  visitDate: string;
  value: number;
}

export interface ProgressResponse {
  patientId: number;
  metric: OutcomeMetric | 'vas_pain';
  unit: string;
  data: ProgressPoint[];
}

// Shared constants
export const AUDIO_MAX_BYTES = 25 * 1024 * 1024; // 25 MB
export const AUDIO_MAX_DURATION_SEC = 30 * 60; // 30 minutes
